import React, { useState, ForwardRefRenderFunction, forwardRef, useImperativeHandle } from "react";

import { Grid } from "@mui/material";
import { Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { FiSend } from "react-icons/fi";

import MarineTrafficService from "../../../services/marine.traffic.service";
import { checkError } from "../../../util/checkError";
import { ShareNotificationId, ShareType } from "../../../util/enums";
import { Button } from "../../Form";
import AutoSuggest from "../../Form/AutoSuggest";
import { Color } from "../../Form/Button";
import Modal, { ModalType } from "../../Generic/Modal";
import { NotificationToast } from "../Toast/NotificationToast";
import useStyles from "./styles";

export interface ShareShipmentModalHandler {
  handleSetEmailsAndPhones: (emails: string[], phones: string[]) => void;
  setOpen: (value: boolean) => void;
  setShipmentId: (value?: number) => void;
}

const ShareShipmentModal: ForwardRefRenderFunction<ShareShipmentModalHandler> = (props, ref) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [shareType, setShareType] = useState<string>(ShareType.SMS);

  const [emails, setEmails] = useState<string[]>([]);
  const [phones, setPhones] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>();

  const [emailOrSmsSent, setEmailOrSmsSent] = useState<boolean>();
  const [shipmentId, setShipmentId] = useState<number>();

  const handleSetEmailsAndPhones = (emails: string[], phones: string[]) => {
    setEmails(emails);
    setPhones(phones);
  };

  useImperativeHandle(ref, () => ({
    handleSetEmailsAndPhones,
    setOpen,
    setShipmentId,
  }));

  const { mutate: shareEmail, isLoading: isLoadingEmail } = useMutation(
    ["users.confirm.email"],
    (share: { shipmentId: number; emails: string[] }) => MarineTrafficService.sendEmail(share),
    {
      onSuccess: () => {
        setEmailOrSmsSent(true);
        setTimeout(() => setEmailOrSmsSent(false), 3000);
        NotificationToast.success(translate("communicationSentSuccessfully"), {
          id: ShareNotificationId.SHARE_SHIPMENT_EMAIL,
        });
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const { mutate: shareSMS, isLoading: isLoadingSMS } = useMutation(
    ["users.confirm.sms"],
    (share: { shipmentId: number; phones: string[] }) => MarineTrafficService.sendSms(share),
    {
      onSuccess: () => {
        setEmailOrSmsSent(true);
        setTimeout(() => setEmailOrSmsSent(false), 3000);
        NotificationToast.success(translate("communicationSentSuccessfully"), {
          id: ShareNotificationId.SHARE_SHIPMENT_SMS,
        });
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const handleSendEmail = () => {
    if (shipmentId) shareEmail({ shipmentId, emails });
  };

  const handleSendSMS = () => {
    if (shipmentId) shareSMS({ shipmentId, phones });
  };

  const handleSend = () => {
    if (shareType === ShareType.EMAIL) {
      handleSendEmail();
    } else {
      handleSendSMS();
    }
  };

  const renderButtons = (isLoading: boolean, isDisabled: boolean) => {
    return (
      <Grid item container md={12} lg={12} justifyContent="flex-end">
        <Button
          disabled={isLoading}
          onClick={() => {
            setOpen(false);
          }}
          customColor={Color.SEND}
          className={classes.buttonCancel}
        >
          {translate("cancel")}
        </Button>

        <Button
          disabled={isLoading || isDisabled}
          onClick={handleSend}
          customColor={Color.SECONDARY_DARK}
          className={classes.buttonShare}
          loading={isLoading}
        >
          {emailOrSmsSent ? (
            <FaCheck size={14} color="#FFFFFF" className="mr-2" />
          ) : (
            <FiSend size={14} color="#FFFFFF" className="mr-2" />
          )}
          {emailOrSmsSent ? translate("sent") : translate("send")}
        </Button>
      </Grid>
    );
  };

  const renderEmailComponent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <AutoSuggest
          placeholder="contact@email.com"
          value={emails}
          disabled={isLoadingEmail}
          multiple
          freeSolo
          getOptionLabel={(option: string) => option}
          onChange={(emails: string[]) => {
            setEmails(emails || []);
          }}
        />
      </Grid>
      {renderButtons(isLoadingEmail, !emails || !emails?.length)}
    </Grid>
  );

  const renderSMSComponent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <AutoSuggest
          placeholder={translate("typeAndClickEnterToAdd")}
          value={phones}
          disabled={isLoadingSMS}
          multiple
          freeSolo
          getOptionLabel={(option: string) => option}
          onChange={(phones: string[]) => {
            setPhones(phones || []);
          }}
        />
      </Grid>
      {renderButtons(isLoadingSMS, !phones || !phones?.length)}
    </Grid>
  );

  const renderShareComponent = () => {
    switch (shareType) {
      case ShareType.SMS:
        return renderSMSComponent();
      case ShareType.EMAIL:
        return renderEmailComponent();
      default:
        return renderEmailComponent();
    }
  };

  const setActiveItem = (value: string): string => {
    if (value === shareType) return "active";

    return "";
  };

  const customTitle = () => {
    return (
      <div>
        <div>{translate("share")}</div>
        <div className={classes.subtitle}>{translate("sendTrackingLinkToACustomer")}</div>
      </div>
    );
  };

  return (
    <Modal
      showActionButtons={false}
      showModalFooter={false}
      type={ModalType.SHARE}
      open={open || false}
      handleClose={() => {
        setOpen(false);
      }}
      title={customTitle()}
      className={classes.modal}
    >
      <Stack direction="column">
        <ul className={classes.shareTypeList}>
          <li className={setActiveItem(ShareType.SMS)} onClick={() => setShareType(ShareType.SMS)}>
            {translate("sms")}
          </li>
          <li
            className={setActiveItem(ShareType.EMAIL)}
            onClick={() => setShareType(ShareType.EMAIL)}
          >
            {translate("email")}
          </li>
        </ul>
      </Stack>
      {renderShareComponent()}
    </Modal>
  );
};

export default forwardRef(ShareShipmentModal);
